import { AxiosError } from 'axios';
import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

import {
  AvailableReferenceData,
  ReferenceContact,
  ReferenceRequestPayload,
  ReferenceToSubmit,
} from '../types/referenceTypes';
import { EmailTemplate } from 'components/CandidatesList/MarketplaceProspectQuickView/types';
import { HiringRequestListAPIData } from '../types';
import { hiringRequestQueries } from 'queryKeys/hiringRequest';
import { ReferenceRequest } from 'types';
import { Reference as ReferenceResponse } from 'types';
import { ReferenceCheck } from 'types';
import EmailTemplatesAPI from 'api/emailTemplatesAPI';
import hiringRequestsAPI from 'api/hiringRequestsAPI';
import referenceCheckAPI from 'api/referenceCheckAPI';
import referenceCheckV2API from 'api/referenceCheckV2API';
import ReferenceRequestsAPI from 'api/referenceRequests';
import ReferencesAPI from 'api/referencesAPI';
import referencesAPI from 'api/referencesAPI';
import referenceTemplateListAPI from 'api/referenceTemplateListAPI';
import usersAPI from 'api/usersAPI';
import { HiringRequestReferenceCheck } from './types';

export const createHiringRequestReference = (
  payload: ReferenceToSubmit | FormData
): Promise<HiringRequestReferenceCheck> => {
  return referenceCheckV2API.create(payload).then((res) => res);
};

export const createReference = (payload: ReferenceToSubmit): Promise<ReferenceResponse> =>
  ReferencesAPI.createReference(payload).then((res) => res);

export const deleteHiringRequestReference = (referenceId: number): Promise<''> => {
  return referenceCheckAPI.destroy(referenceId).then((res) => res);
};

export const editHiringRequestReference = (
  referenceId: number,
  payload: ReferenceToSubmit | FormData
): Promise<HiringRequestReferenceCheck> => {
  return referenceCheckV2API.edit(referenceId, payload).then((res) => res);
};

export const requestReference = (candidateId: number, payload: ReferenceRequestPayload) =>
  ReferenceRequestsAPI.postRequest(candidateId, payload).then((res) => res);

export const useQueryGetReferenceTemplates = (
  districtId: number,
  options?: Omit<UseQueryOptions<AvailableReferenceData[], AxiosError>, 'queryKey' | 'queryFn'>
): UseQueryResult<AvailableReferenceData[], AxiosError> => {
  return useQuery({
    queryKey: hiringRequestQueries.hiringRequests.referenceTemplates(districtId).queryKey,
    queryFn: () => referenceTemplateListAPI.fetchReferences().then((res) => res),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useQueryGetReferenceContacts = (
  candidateId: number
): UseQueryResult<ReferenceContact[], AxiosError> => {
  return useQuery({
    queryKey: hiringRequestQueries.hiringRequests.referenceContacts(candidateId).queryKey,
    queryFn: () => referencesAPI.getReferences(candidateId).then((res) => res),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const getEmailTemplatesForReferences = () =>
  EmailTemplatesAPI.getTemplatesForReferences().then((res) => res);

export const useQueryGetEmailTemplatesForReferences = (
  candidateId: number,
  options?: Omit<UseQueryOptions<EmailTemplate[], AxiosError>, 'queryKey' | 'queryFn'>
): UseQueryResult<EmailTemplate[], AxiosError> => {
  return useQuery({
    queryKey: hiringRequestQueries.hiringRequests.emailTemplates(candidateId).queryKey,
    queryFn: () => getEmailTemplatesForReferences().then((res) => res),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useQueryGetCompletedAndDraftReferencesForHiringRequest = (
  userId: number,
  options?: Omit<UseQueryOptions<ReferenceCheck[], AxiosError>, 'queryKey' | 'queryFn'>
): UseQueryResult<ReferenceCheck[], AxiosError> => {
  return useQuery({
    queryKey: hiringRequestQueries.hiringRequests.completedAndDraftReferences(userId).queryKey,
    queryFn: () =>
      usersAPI.fetchReferenceChecks(userId).then((res) => {
        return res;
      }),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useQueryPendingReferencesForHiringRequest = (
  candidateId: number,
  options?: Omit<UseQueryOptions<ReferenceRequest[], AxiosError>, 'queryKey' | 'queryFn'>
): UseQueryResult<ReferenceRequest[], AxiosError> => {
  return useQuery({
    queryKey: hiringRequestQueries.hiringRequests.pendingReferences(candidateId).queryKey,
    queryFn: () =>
      ReferenceRequestsAPI.getRequests(candidateId).then((res) => {
        return res;
      }),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useQueryGetHiringRequestListData = (
  districtId: number,
  options?: Omit<UseQueryOptions<HiringRequestListAPIData, AxiosError>, 'queryKey' | 'queryFn'>
): UseQueryResult<HiringRequestListAPIData, AxiosError> => {
  return useQuery({
    queryKey: hiringRequestQueries.hiringRequests.list(districtId).queryKey,
    queryFn: () => hiringRequestsAPI.getHiringRequests().then((res) => res),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...options,
  });
};
